/**
 * T-55: questions the software factory's AI builder asks on a task mirrored from jflo. The factory
 * posts a comment holding one fenced block, the same one its coding agent writes (software-factory's
 * shared/factory-questions.ts, kept in step with it):
 *
 * ```factory-questions
 * { "ask": "sam@example.com", "questions": [{ "id": "scope", "header": "Scope", "question": "…",
 *   "options": [{ "label": "Just the API", "description": "…" }], "multiSelect": false }] }
 * ```
 *
 * The task shows it as a question card (FactoryQuestionsCard) and, when `ask` names someone on the
 * team, waits on them. Their answers go back as one comment in the factory's own Answers format, so
 * the factory reads them the way it reads answers given on its own board.
 */

import { TASK_CHAT_MAX } from './types/tasks';

export const FACTORY_QUESTIONS_LANG = 'factory-questions';

/** Most questions in one round, options per question, and characters per piece of text. */
export const FACTORY_QUESTIONS_MAX = 8;
export const FACTORY_OPTIONS_MAX = 8;
/** Characters per piece of text: a question, an option's description, or one answer written on the card. */
export const TEXT_MAX = 1000;

export interface FactoryQuestionOption {
  label: string;
  description?: string;
}

export interface FactoryQuestion {
  id: string;
  /** A short label, e.g. "Scope". */
  header?: string;
  question: string;
  /** None: answered in words. */
  options: FactoryQuestionOption[];
  multiSelect: boolean;
}

export interface FactoryQuestions {
  /** Who should answer: an email or a name, as the agent wrote it. Null: the person who asked the AI builder. */
  ask: string | null;
  questions: FactoryQuestion[];
}

const text = (v: unknown, max = TEXT_MAX): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

/** The block's JSON, checked and trimmed; null when it isn't usable. */
export function parseFactoryQuestionsJson(raw: string): FactoryQuestions | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  // A bare list of questions is accepted too.
  const obj = (Array.isArray(data) ? { questions: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.questions)) return null;
  const questions: FactoryQuestion[] = [];
  const ids = new Set<string>();
  for (const [i, item] of obj.questions.slice(0, FACTORY_QUESTIONS_MAX).entries()) {
    const q = (item ?? {}) as Record<string, unknown>;
    const question = text(q.question);
    if (!question) continue;
    const options = (Array.isArray(q.options) ? q.options : [])
      .slice(0, FACTORY_OPTIONS_MAX)
      .map((o): FactoryQuestionOption | null => {
        if (typeof o === 'string') return text(o, 200) ? { label: text(o, 200) } : null;
        const opt = (o ?? {}) as Record<string, unknown>;
        const label = text(opt.label, 200);
        if (!label) return null;
        const description = text(opt.description);
        return description ? { label, description } : { label };
      })
      .filter((o): o is FactoryQuestionOption => o !== null);
    const header = text(q.header, 60);
    // Ids key the answers on the card: each one its own (jflo, T-55).
    let id = text(q.id, 60) || `q${i + 1}`;
    if (ids.has(id)) id = `${id}-${i + 1}`;
    ids.add(id);
    questions.push({
      id,
      ...(header ? { header } : {}),
      question,
      options,
      multiSelect: q.multiSelect === true,
    });
  }
  if (!questions.length) return null;
  return { ask: text(obj.ask, 200) || null, questions };
}

/**
 * The first fenced block in a message whose language is `lang` (```lang or ~~~lang), with where it
 * sits: its body, and the offsets of the whole block. Null when there's none. Also used for
 * factory-suggestions.
 */
export function findFencedBlock(message: string, lang: string): { body: string; start: number; end: number } | null {
  const body = message.replace(/\r\n?/g, '\n');
  const open = new RegExp('^ {0,3}(`{3,}|~{3,})[ \\t]*' + lang + '[ \\t]*$', 'm').exec(body);
  if (!open) return null;
  const fence = open[1];
  const bodyStart = open.index + open[0].length + 1;
  const close = new RegExp(`^ {0,3}${fence[0] === '`' ? '`' : '~'}{${fence.length},}[ \\t]*$`, 'm').exec(body.slice(bodyStart));
  const bodyEnd = close ? bodyStart + close.index : body.length;
  return { body: body.slice(bodyStart, bodyEnd), start: open.index, end: close ? bodyStart + close.index + close[0].length : body.length };
}

/** The first factory-questions block in a message, with where it sits; null when there's none (or it's broken). */
export function findFactoryQuestions(message: string): { questions: FactoryQuestions; start: number; end: number } | null {
  const block = findFencedBlock(message, FACTORY_QUESTIONS_LANG);
  const questions = block ? parseFactoryQuestionsJson(block.body) : null;
  if (!block || !questions) return null;
  return { questions, start: block.start, end: block.end };
}

/** One question's answer: the options picked and anything written. */
export interface FactoryAnswer {
  picked: string[];
  other: string;
}

/**
 * A question's answer, read as the map's own entry only: an id like `constructor` or `__proto__`
 * mustn't find what every object inherits (jflo, T-55).
 */
export function answerFor(answers: Record<string, FactoryAnswer | undefined>, id: string): FactoryAnswer | undefined {
  return Object.prototype.hasOwnProperty.call(answers, id) ? answers[id] : undefined;
}

/** True when every question has an answer. */
export function answersComplete(questions: FactoryQuestion[], answers: Record<string, FactoryAnswer | undefined>): boolean {
  return questions.every((q) => {
    const a = answerFor(answers, q.id);
    return Boolean(a && (a.picked.length || a.other.trim()));
  });
}

/** The answers as the chat message the person posts (and the agent reads). */
export function formatFactoryAnswers(questions: FactoryQuestion[], answers: Record<string, FactoryAnswer | undefined>): string {
  const lines = ['**Answers**', ''];
  questions.forEach((q, i) => {
    const a = answerFor(answers, q.id);
    const parts = [...(a?.picked ?? []), ...(a?.other.trim() ? [a.other.trim()] : [])];
    lines.push(`${i + 1}. ${q.header ? `**${q.header}:** ` : ''}${q.question.replace(/\n+/g, ' ')}`);
    lines.push(`   → ${parts.length ? parts.join('; ').replace(/\n+/g, ' ') : '_(no answer)_'}`);
  });
  return lines.join('\n');
}

/**
 * Reads back a message formatFactoryAnswers wrote, one answer per question in order, so the
 * task's Decisions can show each question with its answer. Null when the message isn't one.
 * An unanswered question reads as null.
 */
export function parseFactoryAnswers(message: string): (string | null)[] | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  if (lines[0]?.trim() !== '**Answers**') return null;
  const answers: (string | null)[] = [];
  for (const line of lines.slice(1)) {
    if (/^\d+\.\s/.test(line)) answers.push(null);
    else {
      const answer = /^\s+→\s?(.*)$/.exec(line);
      if (answer && answers.length && answers[answers.length - 1] === null) {
        const text = answer[1].trim();
        answers[answers.length - 1] = text && text !== '_(no answer)_' ? text : null;
      }
    }
  }
  return answers.length ? answers : null;
}

/**
 * The first line of a reply to a round written in words (the card's "Reply in words"
 * button), in place of picking an answer per question. Like an Answers message, it closes the round
 * it replies to; a plain comment never does.
 */
const QUESTIONS_REPLY_HEAD = '**Reply to the questions**';

/**
 * The longest reply in words: what fits in a task comment (TASK_CHAT_MAX) with the heading, so the server never cuts it short.
 */
export const QUESTIONS_REPLY_MAX = TASK_CHAT_MAX - QUESTIONS_REPLY_HEAD.length - 2;

/** A reply in words to a round of questions, as the chat message the person posts (and the agent reads). */
export function formatQuestionsReply(text: string): string {
  return `${QUESTIONS_REPLY_HEAD}\n\n${text.trim()}`;
}

/** The text of a reply formatQuestionsReply wrote; null when the message isn't one. */
export function parseQuestionsReply(message: string): string | null {
  const body = message.replace(/\r\n?/g, '\n').trim();
  if (!body.startsWith(QUESTIONS_REPLY_HEAD)) return null;
  const rest = body.slice(QUESTIONS_REPLY_HEAD.length);
  if (rest && !rest.startsWith('\n')) return null;
  return rest.trim();
}
